import type { MapUserConfig } from 'components/map/types';
import { WELL_STATUS_COLOR_MAP } from 'utils/well-status';
import {
  SLOT_CATEGORY_LABELS,
  WELL_STATUS_SLOT_CATEGORY_KEYS,
} from 'utils/slot-categories';

// `value` is what the wells query returns; `optionKey` is the stem the `wellsV2`
// options key on; `color` mirrors `components/map/wells-v2-layer.tsx`'s own palette.
export interface WellStatus {
  value: string;
  optionKey: string;
  label: string;
  color: string;
}

// The main map's option-key stem per Create New Well status (horizontalPrePermit, …).
const NON_DERIVED_OPTION_KEYS: Readonly<Record<string, string>> = {
  proposal: 'Proposal',
  planned: 'Planned',
  pre_permit: 'PrePermit',
  apd: 'Apd',
};

/** In the order the mock's legend lists them. */
export const WELL_STATUSES: readonly WellStatus[] = [
  { value: 'permit', optionKey: 'Permit', label: 'Permit', color: '#ff69b4' },
  { value: 'duc', optionKey: 'Duc', label: 'DUC', color: '#fe9929' },
  {
    value: 'producing',
    optionKey: 'Producing',
    label: 'Producing',
    color: '#4292c6',
  },
  { value: 'shut_in', optionKey: 'ShutIn', label: 'Shut In', color: '#555555' },
  {
    value: 'expired_permit',
    optionKey: 'ExpiredPermit',
    label: 'Expired Permit',
    color: '#969696',
  },
  {
    value: 'inactive',
    optionKey: 'Inactive',
    label: 'Inactive',
    color: '#cccccc',
  },
  // The four Create New Well statuses, one legend row each, in display order (INS-13300).
  // Without them the page's overlay found no colour for these wells and skipped them, and the
  // vertical ones stayed hidden under `vertical*: false`.
  ...WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) => ({
    value: status,
    optionKey: NON_DERIVED_OPTION_KEYS[status],
    label: SLOT_CATEGORY_LABELS[status],
    color: WELL_STATUS_COLOR_MAP[status],
  })),
];

/** Each status the map paints: every legend row is one status. */
export const PAINTED_WELL_STATUSES: readonly WellStatus[] = WELL_STATUSES;

/** The default colour per status, as the map's own wells layer paints them. */
export const WELL_STATUS_COLORS: Record<string, string> = Object.fromEntries(
  PAINTED_WELL_STATUSES.map((status) => [status.value, status.color])
);

type WellsOptions = MapUserConfig['wellsV2']['options'];

// The three keys a status spans — horizontal, vertical, and the bare stem for an
// unknown direction. The map's own selector writes all three, so this does too.
const optionKeysFor = (key: string): Array<keyof WellsOptions> => {
  const bare = (key.charAt(0).toLowerCase() +
    key.slice(1)) as keyof WellsOptions;
  return [
    `horizontal${key}` as keyof WellsOptions,
    `vertical${key}` as keyof WellsOptions,
    bare,
  ];
};

// Every `wellsV2` option for wells shown or hidden. One definition for both the
// starting config and the rail's toggle, so "checked but nothing paints" can't happen.
export const wellsLayerOptions = (visible: boolean): WellsOptions => {
  const options = {
    // Bore ends aren't part of this page's wells layer; the mock draws lines only.
    surfaceHole: false,
    bottomHole: false,
    stick: visible,
    survey: visible,
    horizontal: visible,
    vertical: visible,
  } as WellsOptions;

  PAINTED_WELL_STATUSES.forEach((status) => {
    optionKeysFor(status.optionKey).forEach((option) => {
      options[option] = visible;
    });
  });

  return options;
};

// A status as a person reads it now lives in `utils/well-status`, backed by
// `enums/well_status_types.json` — one map for this legend and the eval surfaces.
// The six statuses above label identically there, and it also names the ones this
// legend omits (abandoned, needs_review, unknown, LTD).
